import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, workspaceAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);

  const loadUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      
      // Load workspaces
      const wsResponse = await workspaceAPI.list();
      setWorkspaces(wsResponse.data);
      
      // Set current workspace from localStorage or first available
      const savedWsId = localStorage.getItem('currentWorkspace');
      const ws = wsResponse.data.find(w => w.id === savedWsId) || wsResponse.data[0];
      if (ws) {
        setCurrentWorkspace(ws);
        localStorage.setItem('currentWorkspace', ws.id);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Load workspaces after login
    const wsResponse = await workspaceAPI.list();
    setWorkspaces(wsResponse.data);
    
    if (wsResponse.data.length > 0) {
      setCurrentWorkspace(wsResponse.data[0]);
      localStorage.setItem('currentWorkspace', wsResponse.data[0].id);
    }
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password, role: 'operator' });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentWorkspace');
    setToken(null);
    setUser(null);
    setWorkspaces([]);
    setCurrentWorkspace(null);
  };

  const switchWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
    localStorage.setItem('currentWorkspace', workspace.id);
  };

  const refreshWorkspaces = async () => {
    const wsResponse = await workspaceAPI.list();
    setWorkspaces(wsResponse.data);
    return wsResponse.data;
  };

  const value = {
    user,
    token,
    loading,
    workspaces,
    currentWorkspace,
    login,
    register,
    logout,
    switchWorkspace,
    refreshWorkspaces,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
